import {AppBar, Box, Toolbar, Stack, Drawer, Grid, Button, Typography, ListItemIcon} from '@mui/material';
import {useMediaQuery} from '@mui/material';
import {useContext, useState, useRef} from 'react';
import AuthContext from '../../context/AuthContext';
import SideBarMenu from '../../components/sideBar/sideBarMenu';
import {TriggerButton} from '../../components/sideBar/sideBarMenu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import mainLogo from '../../images/logo_black_letters_2.png';
import MiscContext from '../../context/MiscContext';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck} from '@fortawesome/free-solid-svg-icons';
import {mainLinearGradient} from '../../theme/gradients';
import {paymentsEnabled} from '../../constants';
import InformativeDialog from '../dialogbox/informativeDialog';
import {useActivityClickApi} from '../../api/activityClick';
import {PricingPlansPopup} from '../pricingOptions/pricingPlansPopup';

/**
 * Header component that displays the navigation bar.
 *
 * @component
 * @returns {JSX.Element} The Header component.
 */
const Header = () => {
  const {isMenuOpen, handleDrawerClose} = useContext(AuthContext);
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const {userPlan} = useContext(MiscContext);
  const activityClickApi = useActivityClickApi();
  const [paymentsPopupOpen, setPaymentsPopupOpen] = useState(false);
  const [informativeDialog, setInformativeDialog] = useState({
    title: '',
    subTitle: '',
    isOpen: false,
    width: 'sm',
  });

  const handleUpgradeClick = async () => {
    //Save in DB click activity of corresponding button
    activityClickApi.postActivityClick('premium_button');

    if (!paymentsEnabled) {
      //open modal popup (this for first released which does not include premium version)
      setInformativeDialog({
        title: 'Coming soon',
        subTitle: `Our premium version has not been released, we'll update you when it is!`,
        isOpen: true,
        width: 'sm',
        onConfirm: () => {
          setInformativeDialog(prev => {
            return {...prev, isOpen: false};
          });
        },
      });
      return;
    }
    setPaymentsPopupOpen(true);
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        zIndex: 300,
        top: 0,
      }}
    >
      {!isSmallScreen && (
        <Box
          sx={{
            backgroundColor: 'white',
            position: 'sticky',
            zIndex: 300,
            top: 0,
            boxSizing: 'border-box',
            boxShadow: '0px 14px 65px -40px rgba(0,0,0,0.69)',
          }}
        >
          <AppBar position="static" sx={{backgroundColor: 'white', pt: 1}} elevation={0}>
            <Toolbar sx={{justifyContent: 'space-between'}}>
              <Box sx={{display: 'flex', alignItems: 'center'}}>
                <img src={mainLogo} height={60} />
              </Box>
              <Box sx={{display: 'flex', alignItems: 'center'}}>
                {userPlan === 'premium' ? (
                  <Typography
                    variant="h6"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '16px',
                      fontWeight: '700',
                      mr: 1,
                      background: 'linear-gradient(91.02deg, #31A6EE -6.18%, #35C6C9 43.59%, #A6E177 94.47%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{justifyContent: 'right', mr: 1}}>
                      <FontAwesomeIcon icon={faCheck} size="sm" style={{color: '#3DA9F8'}} />
                    </ListItemIcon>
                    Upgraded to Premium
                  </Typography>
                ) : userPlan === 'basic' ? (
                  <Button
                    variant="contained"
                    aria-label="upgradePremium"
                    component="label"
                    sx={{
                      background: `linear-gradient(91.76deg, #31A6EE 0.45%, #35C6C9 53.85%, #A6E177 100%)`,
                      borderColor: mainLinearGradient,
                      color: 'white',
                      borderRadius: 5,
                      fontWeight: 600,
                      fontSize: 14,
                      mr: 3,
                      borderWidth: 3,
                    }}
                    onClick={handleUpgradeClick}
                  >
                    Upgrade to Premium
                  </Button>
                ) : null}
                <TriggerButton
                  iconButtonStyles={{
                    mr: 6,
                  }}
                />
              </Box>
            </Toolbar>
          </AppBar>
        </Box>
      )}
      <Drawer anchor="right" open={isMenuOpen} onClose={handleDrawerClose}>
        <SideBarMenu />
      </Drawer>

      {isSmallScreen && (
        <Grid
          container
          alignItems="center"
          justifyContent="flex-start"
          p={1}
          pt={1}
          sx={{backgroundColor: 'white'}}
          columnGap={3}
        >
          <Grid item xs={12}>
            <AppBar position="static" sx={{backgroundColor: 'white', pt: 1}} elevation={0}>
              <Toolbar
                sx={{
                  justifyContent: 'space-between',
                  backgroundColor: 'white',
                }}
              >
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                  <img src={mainLogo} height={60} />
                </Box>
                <TriggerButton />
              </Toolbar>
            </AppBar>
          </Grid>
        </Grid>
      )}
      <PricingPlansPopup open={paymentsPopupOpen} setOpen={setPaymentsPopupOpen} />
      {/* Modal dialog box when user clicks upgrade to premium button */}
      <InformativeDialog informativeDialog={informativeDialog} setInformativeDialog={setInformativeDialog} />
    </Box>
  );
};

/**
 * Footer component that displays the navigation button for navigating back.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.handleBack - The function to handle the back button click.
 * @param {string} props.backButtonName - The name of the back button.
 * @returns {JSX.Element} The Footer component.
 */
const Footer = ({handleBack, backButtonName}) => {
  const textStyles = {
    fontSize: 14,
    fontWeight: 500,
    width: 'fit-content',
  };

  return (
    <Stack
      key={'footer'}
      direction="row"
      justifyContent="space-between"
      sx={{
        p: 3,
        py: 2,
        position: 'sticky',
        bottom: 0,
        zIndex: 100,
        backgroundColor: '#FBFBFB',
      }}
    >
      <Button onClick={handleBack} disabled={backButtonName === ''} startIcon={<ArrowBackIcon />}>
        <Typography sx={textStyles} textAlign="left">
          {backButtonName}
        </Typography>
      </Button>
    </Stack>
  );
};

/**
 * Component representing the profile information base layout.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The content to be displayed in the layout.
 * @param {Function} props.handleBack - The function to handle the back button click.
 * @param {string} props.backButtonName - The name of the back button.
 * @returns {JSX.Element} The ProfileInfoBase component.
 */
export const ProfileInfoBase = ({children, handleBack, backButtonName}) => {
  return (
    <Stack minHeight="100vh" justifyContent="space-between">
      <Header />
      <Stack
        flexGrow={1}
        alignItems="center"
        justifyContent="flex-start"
        sx={{
          marginX: {
            xs: 3,
            sm: 2,
            md: 10,
            lg: 10,
          },
          marginTop: {
            xs: 0,
            sm: 1,
            md: 2,
          },
          boxSizing: 'border-box',
        }}
      >
        {children}
      </Stack>
      <Footer handleBack={handleBack} backButtonName={backButtonName} />
    </Stack>
  );
};
